import {
  TextField,
  Autocomplete,
  Stack,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { Controller, Control, FieldValues } from 'react-hook-form';

export interface AutocompleteSelectOptions {
  label: string;
  value: number | string;
  description?: string;
}

const filterOptions = createFilterOptions<AutocompleteSelectOptions>({
  stringify: (option) =>
    [option.label, option.description].filter(Boolean).join(' '),
});

interface ControlledSelectInputProps<T extends FieldValues> {
  control: Control<T>;
  label: string;
  name: string;
  options: AutocompleteSelectOptions[];
  controllerProps?: any;
  inputsProps?: any;
  errors: any;
  isDirty: boolean;
  disablePortal?: boolean;
  disabled?: boolean;
  isMultiple?: boolean;
}

const ControlledSelectInput = <T extends FieldValues>(
  props: ControlledSelectInputProps<T>
) => {
  const { disablePortal = true, isMultiple = false } = props;

  return (
    <Controller
      {...props.controllerProps}
      control={props.control}
      name={props.name}
      render={({ field: { value, onChange } }) => {
        const selectedOption = isMultiple
          ? props.options.filter((option) => value?.includes(option.value))
          : props.options.find((option) => option.value === value) || null;

        return (
          <Autocomplete
            multiple={isMultiple}
            value={selectedOption}
            filterOptions={filterOptions}
            {...props.inputsProps}
            disablePortal={disablePortal}
            disabled={props.disabled}
            options={props.options}
            getOptionLabel={(option: AutocompleteSelectOptions) => option.label}
            isOptionEqualToValue={(
              option: AutocompleteSelectOptions,
              value: AutocompleteSelectOptions
            ) => option.value === value.value}
            renderOption={(props, option: AutocompleteSelectOptions) => (
              <li {...props} key={option.value}>
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Typography>{option.label}</Typography>
                  {option.description && (
                    <Typography variant="body2" color="text.secondary">
                      {option.description}
                    </Typography>
                  )}
                </Stack>
              </li>
            )}
            renderInput={(params: any) => (
              <TextField
                {...params}
                label={props.label}
                helperText={
                  props.isDirty && props.errors && props.errors.message
                }
                error={props.isDirty && Boolean(props.errors)}
              />
            )}
            onChange={(e: any, data: any) => {
              if (isMultiple) {
                onChange(
                  data.map((option: AutocompleteSelectOptions) => option.value)
                );
              } else {
                onChange(data?.value || null);
              }
            }}
          />
        );
      }}
    />
  );
};

export default ControlledSelectInput;
